// apps/web/src/routes/channels.tsx
import { useState } from 'react'
import useSWR from 'swr'
import { Card, CardContent, CardHeader } from '@viecxanh/chat-ui/card'
import { Button } from '@viecxanh/chat-ui/button'
import { Badge } from '@viecxanh/chat-ui/badge'
import { Switch } from '@viecxanh/chat-ui/switch'
import { Input } from '@viecxanh/chat-ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@viecxanh/chat-ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@viecxanh/chat-ui/dialog'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@viecxanh/chat-ui/alert-dialog'
import { api } from '../lib/api-client'
import type { Channel } from '@viecxanh/chat-shared'

const CHANNEL_LABELS: Record<string, string> = {
  facebook: 'Facebook',
  zalo_oa: 'Zalo OA',
  widget: 'Widget',
  internal: 'Nội bộ',
}

const STATUS_COLORS: Record<string, string> = {
  connected: 'bg-green-100 text-green-700',
  error: 'bg-red-100 text-red-700',
  pending: 'bg-yellow-100 text-yellow-700',
}

export function ChannelsPage() {
  const { data: channels = [], isLoading, mutate } = useSWR<Channel[]>(
    '/api/admin/channels',
    (url: string) => api.get<Channel[]>(url),
  )

  const [showAdd, setShowAdd] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)
  const [form, setForm] = useState({ name: '', type: 'facebook', page_id: '', access_token: '', verify_token: '', oa_id: '', secret_key: '', refresh_token: '' })
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      await api.post('/api/admin/channels', form)
      await mutate()
      setShowAdd(false)
    } finally {
      setSubmitting(false)
    }
  }

  const handleToggle = async (id: string, is_active: boolean) => {
    await api.put(`/api/admin/channels/${id}`, { is_active })
    await mutate()
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    await api.delete(`/api/admin/channels/${deleteTarget}`)
    setDeleteTarget(null)
    await mutate()
  }

  const handleTest = async (id: string) => {
    try {
      await api.post(`/api/admin/channels/${id}/test`)
      alert('Kết nối thành công!')
    } catch {
      alert('Kết nối thất bại')
    }
  }

  if (isLoading) return <div className="p-6 text-slate-400">Đang tải...</div>

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Quản lý kênh</h1>
        <Button onClick={() => setShowAdd(true)} className="bg-brand hover:bg-brand-dark">+ Thêm kênh</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {channels.map((ch) => (
          <Card key={ch._id}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{ch.name}</p>
                  <Badge className="mt-1 text-xs">{CHANNEL_LABELS[ch.type] ?? ch.type}</Badge>
                </div>
                <Switch checked={ch.is_active} onCheckedChange={(v) => void handleToggle(ch._id, v)} />
              </div>
            </CardHeader>
            <CardContent>
              <Badge className={`text-xs ${STATUS_COLORS[ch.status] ?? ''}`}>
                {ch.status === 'connected' ? 'Đã kết nối' : ch.status === 'error' ? 'Lỗi' : 'Chờ'}
              </Badge>
              <div className="mt-3 flex gap-2">
                <Button variant="outline" size="sm" onClick={() => void handleTest(ch._id)}>Kiểm tra</Button>
                <Button variant="outline" size="sm" className="text-red-500" onClick={() => setDeleteTarget(ch._id)}>Xóa</Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Thêm kênh mới</DialogTitle>
            <DialogDescription>Kết nối Facebook, Zalo OA hoặc Widget để nhận tin nhắn</DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4">
            <div className="space-y-1">
              <label className="text-sm font-medium">Tên kênh</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Loại kênh</label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="facebook">Facebook</SelectItem>
                  <SelectItem value="zalo_oa">Zalo OA</SelectItem>
                  <SelectItem value="widget">Widget</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.type === 'facebook' && (
              <>
                <Input placeholder="Page ID" value={form.page_id} onChange={(e) => setForm({ ...form, page_id: e.target.value })} />
                <Input placeholder="Page Access Token" value={form.access_token} onChange={(e) => setForm({ ...form, access_token: e.target.value })} />
                <Input placeholder="Webhook Verify Token" value={form.verify_token} onChange={(e) => setForm({ ...form, verify_token: e.target.value })} />
              </>
            )}
            {form.type === 'zalo_oa' && (
              <>
                <Input placeholder="OA ID" value={form.oa_id} onChange={(e) => setForm({ ...form, oa_id: e.target.value })} />
                <Input placeholder="Access Token" value={form.access_token} onChange={(e) => setForm({ ...form, access_token: e.target.value })} />
                <Input placeholder="Secret Key" value={form.secret_key} onChange={(e) => setForm({ ...form, secret_key: e.target.value })} />
                <Input placeholder="Refresh Token" value={form.refresh_token} onChange={(e) => setForm({ ...form, refresh_token: e.target.value })} />
              </>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>Hủy</Button>
              <Button type="submit" disabled={submitting} className="bg-brand hover:bg-brand-dark">
                {submitting ? 'Đang lưu...' : 'Lưu'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Xác nhận xóa kênh?</AlertDialogTitle></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Hủy</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleDelete()} className="bg-red-500 hover:bg-red-600">Xóa</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
